import { useCallback, useEffect, useState } from 'react';
import { commandCenterApi, formatCurrency } from '../../utils/api';
import { toast } from '../../components/Toast';
import Icon from '../../components/Icon';
import { LoadState, openInApp } from './parts';
import { fmt } from './statsParts';

// One suspected pair, opened in place in the חשודים table: everything needed
// to say "same supplier or not" without opening anything else, and the
// decision itself (owner's review with design/UX/PM, 28/09/2026).
//
// "אותו ספק" renames every row of both suppliers, in every restaurant, to the
// chosen name — one click, undoable from "הוחלטו". The suggested name is the
// server's: the side at more restaurants, so the fewest customers see a
// rename. A pair one restaurant holds on both sides needs a MERGE, which the
// console does not do yet — the button says so instead of failing.

function BusinessId({ row }) {
  if (!row.business_id) return <span className="cc-muted">ללא ח.פ.</span>;
  const source = row.business_id_source === 'entered'
    ? 'הוזן'
    : row.read ? `נקרא ב-${fmt(row.read.n)} מתוך ${fmt(row.read.of)}` : 'ממסמכים';
  return <span><bdi>{row.business_id}</bdi> <span className="cc-price-tag">{source}</span></span>;
}

function Side({ side, chosen, onChoose, radioName }) {
  const others = side.variants.filter(v => v !== side.name);
  return (
    <div className={`cc-pair-side${chosen ? ' is-chosen' : ''}`}>
      <label className="cc-pair-pick">
        <input type="radio" name={radioName} checked={chosen} onChange={onChoose} />
        <span className="cc-pair-name">{side.name}</span>
      </label>
      {others.length > 0 && <div className="cc-muted cc-pair-small">נכתב גם: {others.join(' · ')}</div>}
      {side.restaurant_bid && (
        <div className="cc-pill bad" style={{ marginTop: 6 }}>
          הח.פ. {side.restaurant_bid.business_id} הוא של המסעדה {side.restaurant_bid.restaurant_name}
        </div>
      )}
      <ul className="cc-pair-rows">
        {side.rows.map(r => (
          <li key={r.supplier_id}>
            <b>{r.restaurant_name}</b> · {r.docs ? `${fmt(r.docs)} מסמכים` : 'אין מסמכים'}
            {r.name !== side.name && <span className="cc-muted"> · בשם &quot;{r.name}&quot;</span>}
            <div className="cc-pair-small"><BusinessId row={r} /></div>
          </li>
        ))}
      </ul>
      {side.latest_document && (
        <button type="button" className="btn btn-sm btn-outline"
          onClick={() => openInApp(`/document/${side.latest_document.id}`, side.latest_document.restaurant_id)}>
          <Icon name="external" size={13} /> מסמך אחרון{side.latest_document.doc_num ? ` ${side.latest_document.doc_num}` : ''}
          {side.latest_document.date ? ` · ${side.latest_document.date}` : ''}
        </button>
      )}
    </div>
  );
}

function SharedProducts({ shared }) {
  if (!shared.total) return <p className="cc-muted cc-pair-small">אין מוצרים משותפים בקטלוג</p>;
  const price = (v) => (v == null ? '—' : formatCurrency(v));
  return (
    <div className="cc-pair-small">
      <b>{fmt(shared.total)} מוצרים משותפים</b>
      <ul className="cc-pair-products">
        {shared.top.map(p => (
          <li key={`${p.sku}|${p.name}`}>{p.name} — {price(p.a_price)} / {price(p.b_price)}</li>
        ))}
      </ul>
    </div>
  );
}

// Which rows the chosen name renames — the "what will happen" line.
function renamesFor(ev, chosenName) {
  return [...ev.a.rows, ...ev.b.rows].filter(r => r.name !== chosenName);
}

function usePairEvidence(a, b) {
  const [ev, setEv] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(async () => {
    setError(false);
    setEv(null);
    try {
      const r = await commandCenterApi.getSupplierPair(a, b);
      setEv(r.data);
    } catch {
      setError(true);
    }
  }, [a, b]);
  useEffect(() => { load(); }, [load]);
  return { ev, error, load };
}

function OtherName({ radioName, checked, value, onPick, onChange }) {
  return (
    <label className="cc-pair-pick cc-pair-small" style={{ marginTop: 8 }}>
      <input type="radio" name={radioName} checked={checked} onChange={onPick} />
      שם אחר:
      <input className="form-control" style={{ maxWidth: 260 }} value={value} aria-label="שם אחר לספק"
        onFocus={onPick} onChange={e => { onChange(e.target.value); onPick(); }} placeholder="שם הספק המאוחד" />
    </label>
  );
}

// The one ח.פ. entered among the rows, when there is exactly one — the server
// copies it to the rows without one (lib/supplierIdentity.js#shareBusinessId).
function sharedBusinessId(ev) {
  const entered = new Set([...ev.a.rows, ...ev.b.rows].filter(r => r.business_id_source === 'entered').map(r => r.business_id));
  return entered.size === 1 ? [...entered][0] : null;
}

// "What will happen": the restaurants renamed, or why it cannot be unified here.
function Plan({ ev, chosenName }) {
  if (ev.needs_merge.length) {
    return (
      <p className="cc-pair-note warn">
        שני הספקים קיימים באותה מסעדה ({ev.needs_merge.map(r => r.name).join(', ')}) — כדי לאחד צריך מיזוג, שיגיע בשלב הבא.
        אפשר כבר עכשיו לסמן &quot;לא אותו ספק&quot; או לדלג.
      </p>
    );
  }
  if (!chosenName) return null;
  const renames = renamesFor(ev, chosenName);
  const bid = sharedBusinessId(ev);
  return (
    <p className="cc-pair-note">
      {renames.length
        ? <>ישתנה השם ל-&quot;{chosenName}&quot; אצל: {renames.map(r => `${r.restaurant_name}${r.docs ? ` (${fmt(r.docs)} מסמכים)` : ''}`).join(' · ')}.
            {' '}כל השמות ושמות הכינוי של שני הספקים יישמרו כשמות כינוי{bid ? `, והח.פ. ${bid} יועתק לרשומות שאין להן ח.פ.` : '.'}</>
        : 'השמות כבר זהים'}
    </p>
  );
}

function useDecide(pair, onDone) {
  const [busy, setBusy] = useState(false);
  const decide = async (decision, name) => {
    setBusy(true);
    try {
      const r = await commandCenterApi.decideSupplierPair({ decision, a: pair.a.key, b: pair.b.key, name, reasons: pair.reasons });
      toast(decision === 'same'
        ? `אוחד בשם "${r.data.chosen}" · ${fmt(r.data.renamed)} שינויים · ניתן לבטל ב"הוחלטו"`
        : 'סומן: לא אותו ספק');
      onDone();
    } catch (err) {
      toast(err.response?.data?.error || 'השמירה נכשלה — אפשר לנסות שוב', 'error');
      setBusy(false);
    }
  };
  return { busy, decide };
}

function Decision({ pair, ev, onDone, onSkip }) {
  const [pick, setPick] = useState(ev.default_name === ev.a.name ? 'a' : 'b');
  const [custom, setCustom] = useState('');
  const { busy, decide } = useDecide(pair, onDone);
  const chosenName = pick === 'a' ? ev.a.name : pick === 'b' ? ev.b.name : custom.trim();
  const canUnify = !ev.needs_merge.length && chosenName;
  const radioName = `pick-${pair.a.key}|${pair.b.key}`;
  return (
    <>
      <div className="cc-pair-grid">
        <Side side={ev.a} chosen={pick === 'a'} onChoose={() => setPick('a')} radioName={radioName} />
        <Side side={ev.b} chosen={pick === 'b'} onChoose={() => setPick('b')} radioName={radioName} />
      </div>
      <SharedProducts shared={ev.shared_products} />
      <OtherName radioName={radioName} checked={pick === 'custom'} value={custom} onPick={() => setPick('custom')} onChange={setCustom} />
      <Plan ev={ev} chosenName={chosenName} />
      <div className="cc-pair-actions">
        <button type="button" className="btn btn-success" disabled={busy || !canUnify} onClick={() => decide('same', chosenName)}>
          <Icon name="check" size={15} /> אותו ספק{canUnify ? ` — לאחד בשם "${chosenName}"` : ''}
        </button>
        <button type="button" className="btn btn-outline" disabled={busy} onClick={() => decide('different')}>לא אותו ספק</button>
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={onSkip}>דלג</button>
      </div>
    </>
  );
}

export default function SuspectReview({ pair, onDone, onSkip }) {
  const { ev, error, load } = usePairEvidence(pair.a.key, pair.b.key);
  return (
    <div className="cc-pair-review">
      <LoadState loading={!ev && !error} error={error} onRetry={load} empty={false}>
        {ev && <Decision pair={pair} ev={ev} onDone={onDone} onSkip={onSkip} />}
      </LoadState>
    </div>
  );
}
